import React, { Fragment, FunctionComponent, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  phoneNumberValidation,
  requiredValidation,
  onlyLetters,
  pastDateOnly,
  validateIdCard,
  validateIdCardWithSelection,
} from 'helpers/validations';
import { getDocLabel } from 'helpers/form';
import { getCountries } from 'helpers/countries';
import { CURRENCY_OPTIONS } from 'helpers/currencies';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import HebrewDateInput from 'components/inputs/HebrewDateInput';
import Checkbox from 'components/inputs/Checkbox';
import FieldRequired from 'components/commons/FieldRequired';
import ProcedureStartStep from 'components/commons/ProcedureStartStep';
import ProcedureReviewStep from 'components/commons/ProcedureReviewStep';
import { IReviewSection } from 'components/commons/ProcedureReviewStep/types';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IDeathsFormData, IDeathsFormProps } from './types';
import './i18n';

const DeathsForm: FunctionComponent<IDeathsFormProps> = (props: IDeathsFormProps) => {
  const {
    cementeries,
    civilStates,
    files,
    idCardTypes,
    initialValues,
    isEditing,
    onSubmit,
    tribes,
  } = props;
  const [formValues, setFormValues] = useState<IDeathsFormData>(initialValues);
  const countries = useMemo(() => getCountries(), []);
  const [showStartStep, setShowStartStep] = useState<boolean>(!isEditing);

  const today = new Date().toISOString().split('T')[0];

  const tabs = [
    i18next.t<string>('DEATHS_FORM:PERSON'),
    i18next.t<string>('DEATHS_FORM:SPOUSE'),
    i18next.t<string>('DEATHS_FORM:OTHER'),
    i18next.t<string>('DEATHS_FORM:CHILDREN'),
    i18next.t<string>('DEATHS_FORM:LOCATION'),
    i18next.t<string>('DEATHS_FORM:SIGNERS'),
    i18next.t<string>('DEATHS_FORM:PAYMENT'),
    i18next.t<string>('DEATHS_FORM:COMMENTS'),
  ];

  const requiredFilesDescription = [
    `[OBLIGATORIO] ${i18next.t<string>('DEATHS_FORM:ID_CARD_FILE')}: ${i18next.t<string>(
      'PROCEDURES:ID_DECEASED_DESC',
      'DNI Frente y Dorso de la persona fallecida.',
    )}`,
    `[OBLIGATORIO] ${i18next.t<string>('DEATHS_FORM:SIGNERS_ID')}: ${i18next.t<string>(
      'PROCEDURES:ID_SIGNERS_DESC',
      'DNI Frente y Dorso de los dos firmantes responsables.',
    )}`,
    `[OPCIONAL] ${i18next.t<string>('DEATHS_FORM:KETUBA_FILE')}: ${i18next.t<string>(
      'PROCEDURES:KETUBA_DEATH_DESC',
      'Libreta de matrimonio religioso de la persona fallecida.',
    )}`,
    `[OPCIONAL] ${i18next.t<string>('DEATHS_FORM:MARRIAGE_CERTIFICATE_FILE')}: ${i18next.t<string>(
      'PROCEDURES:MARR_CERT_DEATH_DESC',
      'Acta de matrimonio civil del fallecido/a.',
    )}`,
    `[OPCIONAL] ${i18next.t<string>('DEATHS_FORM:GET_FILE')}: ${i18next.t<string>(
      'PROCEDURES:GET_DEATH_DESC',
      'Acta de divorcio religioso (Get) si correspondiera.',
    )}`,
  ];

  const reviewSections: IReviewSection[] = [
    {
      title: i18next.t('DEATHS_FORM:PERSON_INFORMATION'),
      fields: [
        { label: i18next.t('DEATHS_FORM:LAST_NAME'), value: formValues.person?.lastName },
        { label: i18next.t('DEATHS_FORM:NAME'), value: formValues.person?.name },
        { label: i18next.t('DEATHS_FORM:ID_CARD'), value: formValues.person?.idCard },
        { label: i18next.t('DEATHS_FORM:AGE'), value: formValues.person?.age },
        { label: i18next.t('DEATHS_FORM:ADDRESS'), value: formValues.person?.address },
        { label: i18next.t('DEATHS_FORM:BIRTHDATE'), value: formValues.person?.birthdate },
        { label: i18next.t('DEATHS_FORM:DEATH_DATE'), value: formValues.person?.deathDate },
        {
          label: i18next.t('DEATHS_FORM:DEATH_DATE_HEBREW'),
          value: formValues.person?.deathDateHebrew,
        },
        { label: i18next.t('DEATHS_FORM:DEATH_PLACE'), value: formValues.person?.deathPlace },
        { label: i18next.t('DEATHS_FORM:COMMUNITY'), value: formValues.person?.community },
        { label: i18next.t('DEATHS_FORM:CIVIL_STATE'), value: formValues.person?.civilState },
        { label: i18next.t('DEATHS_FORM:TRIBE'), value: formValues.person?.tribe },
      ],
    },
    {
      title: i18next.t('DEATHS_FORM:SPOUSE_INFORMATION'),
      fields: [
        { label: i18next.t('DEATHS_FORM:LAST_NAME'), value: formValues.spouse?.lastName },
        { label: i18next.t('DEATHS_FORM:NAME'), value: formValues.spouse?.name },
        { label: i18next.t('DEATHS_FORM:WEDDING_TEMPLE'), value: formValues.spouse?.weddingTemple },
        { label: i18next.t('DEATHS_FORM:ALIVE'), value: formValues.spouse?.alive },
        { label: i18next.t('DEATHS_FORM:CEMENTERY'), value: formValues.spouse?.cementery },
      ],
    },
    {
      title: i18next.t('DEATHS_FORM:OTHER_INFORMATION'),
      fields: [
        { label: i18next.t('DEATHS_FORM:FATHER_INFO'), value: formValues.family?.fatherInfo },
        { label: i18next.t('DEATHS_FORM:MOTHER_INFO'), value: formValues.family?.motherInfo },
        { label: i18next.t('DEATHS_FORM:CHILDREN_INFO'), value: formValues.family?.childrenInfo },
        {
          label: `${i18next.t('DEATHS_FORM:CEMENTERY')} (Familiar)`,
          value: formValues.family?.cementeries,
        },
      ],
    },
    {
      title: i18next.t('DEATHS_FORM:LOCATION_INFORMATION'),
      fields: [
        { label: i18next.t('DEATHS_FORM:CEMENTERY'), value: formValues.location?.cementery },
        {
          label: i18next.t('RESERVATIONS_FORM:SECTOR', 'Sector'),
          value: formValues.location?.sector,
        },
        {
          label: i18next.t('RESERVATIONS_FORM:PLANK', 'Tablón'),
          value: formValues.location?.plank,
        },
        { label: i18next.t('RESERVATIONS_FORM:ROW', 'Fila'), value: formValues.location?.row },
        {
          label: i18next.t('RESERVATIONS_FORM:GRAVE', 'Sepultura'),
          value: formValues.location?.grave,
        },
        {
          label: i18next.t('RESERVATIONS_FORM:BLOCK', 'Manzana'),
          value: formValues.location?.block,
        },
      ],
    },
    ...(formValues.signers || []).map((signer: any, index: number) => ({
      title: `${i18next.t('DEATHS_FORM:SIGNERS_INFORMATION')} ${index + 1}`,
      fields: [
        { label: i18next.t('DEATHS_FORM:SIGNER_NAME'), value: signer.name },
        { label: i18next.t('DEATHS_FORM:EMAIL'), value: signer.email },
        { label: i18next.t('DEATHS_FORM:ID_CARD'), value: signer.idCard },
        {
          label: i18next.t('DEATHS_FORM:PHONE'),
          value: signer.phone ? `${signer.areaCode || ''} ${signer.phone}` : null,
        },
        { label: i18next.t('DEATHS_FORM:TELEPHONE'), value: signer.telephone },
        { label: i18next.t('DEATHS_FORM:ADDRESS'), value: signer.address },
        { label: i18next.t('DEATHS_FORM:KINSHIP'), value: signer.kinship },
      ],
    })),
    {
      title: i18next.t('DEATHS_FORM:PAYMENT_INFORMATION'),
      fields: [
        { label: i18next.t('DEATHS_FORM:CURRENCY'), value: formValues.payment?.currency },
        { label: i18next.t('DEATHS_FORM:AMOUNT'), value: formValues.payment?.amount },
        {
          label: i18next.t('DEATHS_FORM:PAYMENT_CONDITIONS'),
          value: formValues.payment?.paymentConditions,
        },
        { label: i18next.t('DEATHS_FORM:PAYMENTS_DONE'), value: formValues.payment?.paymentsDone },
        { label: i18next.t('DEATHS_FORM:GARAGE'), value: formValues.payment?.garage },
      ],
    },
    {
      title: i18next.t('DEATHS_FORM:COMMENTS'),
      fields: [{ label: i18next.t('DEATHS_FORM:COMMENTS'), value: formValues.comments }],
    },
  ];

  if (showStartStep) {
    return (
      <ViewContainer auth>
        <ProcedureStartStep
          procedureName={i18next.t<string>('DEATHS_FORM:TITLE')}
          requiredFiles={requiredFilesDescription}
          onStart={() => setShowStartStep(false)}
        />
      </ViewContainer>
    );
  }

  return (
    <ViewContainer auth data-testid="deaths-form">
      <Title>{i18next.t<string>('DEATHS_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IDeathsFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          <MultiStepForm.Page key="person-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:PERSON_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:LAST_NAME')}
                name="person.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:NAME')}
                name="person.name"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DEATHS_FORM:ID_CARD_TYPE')}
                name="person.idCardType"
                parse={(value: string) => value}
                options={idCardTypes}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={getDocLabel('person.idCard', formValues, idCardTypes)}
                name="person.idCard"
                parse={(value: string) => value}
                render={Input}
                validate={validateIdCard(idCardTypes)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DEATHS_FORM:ID_CARD_FILE')}
                name="person.idCardFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DEATHS_FORM:ID_CARD_FILE'),
                    value: files?.personIdCardFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
              <Field
                label={i18next.t<string>('DEATHS_FORM:ID_CARD_BACK_FILE')}
                name="person.idCardBackFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DEATHS_FORM:ID_CARD_BACK_FILE'),
                    value: files?.personIdCardBackFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={fileValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:AGE')}
                name="person.age"
                render={Input}
                type="number"
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('DEATHS_FORM:COUNTRY')}
                name="person.country"
                options={countries}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:ADDRESS')}
                name="person.address"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:BIRTHDATE')}
                name="person.birthdate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:DEATH_DATE')}
                name="person.deathDate"
                render={Input}
                type="date"
                max={today}
                validate={composeValidators(requiredValidation, pastDateOnly)}
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:DEATH_PLACE')}
                name="person.deathPlace"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:DEATH_DATE_HEBREW')}
                name="person.deathDateHebrew"
                parse={(value: string) => value}
                render={HebrewDateInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:COMMUNITY')}
                name="person.community"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DEATHS_FORM:CIVIL_STATE')}
                name="person.civilState"
                Field={civilStates}
                options={civilStates}
                render={SelectInput}
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('DEATHS_FORM:TRIBE')}
                name="person.tribe"
                options={tribes}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DEATHS_FORM:KETUBA_FILE')}
                name="person.ketubaFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DEATHS_FORM:KETUBA_FILE'),
                    value: files?.personKetubaFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
              <Field
                label={i18next.t<string>('DEATHS_FORM:MARRIAGE_CERTIFICATE_FILE')}
                name="person.marriageCertificateFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DEATHS_FORM:MARRIAGE_CERTIFICATE_FILE'),
                    value: files?.personMarriageCertificateFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing || formValues.person.civilState !== 'MARRIED'
                    ? fileValidation
                    : composeValidators(requiredValidation, fileValidation)
                }
              />
            </FormRowContainer>
            {formValues.person.civilState === 'DIVORCED' && (
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:GET_FILE')}
                name="person.getFile"
                previewFiles={[
                  {
                    label: i18next.t<string>('DEATHS_FORM:GET_FILE'),
                    value: files?.personGetFile,
                  },
                ]}
                render={Input}
                type="file"
                validate={
                  isEditing ? fileValidation : composeValidators(requiredValidation, fileValidation)
                }
              />
            )}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="spouse-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:SPOUSE_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:LAST_NAME')}
                name="spouse.lastName"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:NAME')}
                name="spouse.name"
                render={Input}
                validate={composeValidators(requiredValidation, onlyLetters)}
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('DEATHS_FORM:WEDDING_TEMPLE')}
              name="spouse.weddingTemple"
              render={Input}
              validate={composeValidators(requiredValidation, onlyLetters)}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:ALIVE')}
                name="spouse.alive"
                render={Checkbox}
              />
              {!formValues.spouse.alive && (
                <Field
                  label={i18next.t<string>('DEATHS_FORM:CEMENTERY')}
                  name="spouse.cementery"
                  options={cementeries}
                  parse={(value: string) => value}
                  render={SelectInput}
                />
              )}
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="other-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:OTHER_INFORMATION')}
            </Typography>
            <br />
            <FieldRequired
              label={i18next.t<string>('DEATHS_FORM:FATHER_INFO')}
              name="family.fatherInfo"
              render={Input}
              type="textarea"
              validate={requiredValidation}
            />
            <FieldRequired
              label={i18next.t<string>('DEATHS_FORM:MOTHER_INFO')}
              name="family.motherInfo"
              render={Input}
              type="textarea"
              validate={requiredValidation}
            />
            <Field
              label={i18next.t<string>('DEATHS_FORM:CEMENTERY')}
              name="family.cementeries"
              options={cementeries}
              parse={(value) => value}
              render={SelectInput}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="children-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:CHILDREN_INFORMATION')}
            </Typography>
            <br />
            <FieldRequired
              label={i18next.t<string>('DEATHS_FORM:CHILDREN_INFO')}
              name="family.childrenInfo"
              render={Input}
              type="textarea"
              validate={composeValidators(requiredValidation, onlyLetters)}
            />
          </MultiStepForm.Page>
          <MultiStepForm.Page key="location-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:LOCATION_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DEATHS_FORM:CEMENTERY')}
                name="location.cementery"
                options={cementeries}
                render={SelectInput}
                validate={requiredValidation}
              />
            </FormRowContainer>

            <FormRowContainer>
              {formValues.location?.cementery === 'OLD' && (
                <>
                  <FieldRequired
                    label="Sector"
                    name="location.sector"
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label="Tablón"
                    name="location.plank"
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label="Fila"
                    name="location.row"
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label="Sepultura"
                    name="location.grave"
                    render={Input}
                    validate={requiredValidation}
                  />
                </>
              )}

              {formValues.location?.cementery === 'ANNEX' && (
                <>
                  <FieldRequired
                    label="Tablón"
                    name="location.plank"
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label="Sepultura"
                    name="location.grave"
                    render={Input}
                    validate={requiredValidation}
                  />
                </>
              )}

              {formValues.location?.cementery === 'CANNING' && (
                <>
                  <FieldRequired
                    label="Manzana"
                    name="location.block"
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label="Fila"
                    name="location.row"
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label="Sepultura"
                    name="location.grave"
                    render={Input}
                    validate={requiredValidation}
                  />
                </>
              )}
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="signers-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:SIGNERS_INFORMATION')}
            </Typography>
            <br />
            {(formValues.signers as Array<unknown>)?.map((_signer: unknown, i: number) => {
              const isMandatory = i < 2;
              const FieldType = isMandatory ? FieldRequired : Field;

              return (
                <Fragment key={`signer-${i.toString()}`}>
                  <FormRowContainer>
                    <FieldType
                      label={`${i18next.t<string>('DEATHS_FORM:SIGNER_NAME')} ${i + 1}`}
                      name={`signers[${i}].name`}
                      parse={(value: string) => value}
                      render={Input}
                      validate={
                        isMandatory
                          ? composeValidators(requiredValidation, onlyLetters)
                          : onlyLetters
                      }
                    />
                    <FieldType
                      label={i18next.t<string>('DEATHS_FORM:EMAIL')}
                      name={`signers[${i}].email`}
                      parse={(value: string) => value}
                      render={Input}
                      validate={
                        isMandatory
                          ? composeValidators(requiredValidation, emailValidation)
                          : emailValidation
                      }
                    />
                  </FormRowContainer>

                  <FormRowContainer>
                    <FieldType
                      label={i18next.t<string>('DEATHS_FORM:ID_CARD_TYPE')}
                      name={`signers[${i}].idCardType`}
                      options={idCardTypes}
                      parse={(value: string) => value}
                      render={SelectInput}
                      validate={isMandatory ? requiredValidation : undefined}
                    />
                    <FieldType
                      label={getDocLabel(`signers[${i}].idCard`, formValues, idCardTypes)}
                      name={`signers[${i}].idCard`}
                      parse={(value: string) => value}
                      render={Input}
                      validate={validateIdCardWithSelection(
                        idCardTypes,
                        formValues.signers?.[i]?.idCardType,
                        isMandatory,
                      )}
                    />
                  </FormRowContainer>

                  <FormRowContainer>
                    <FieldType
                      label={i18next.t<string>('DEATHS_FORM:ID_CARD_FILE')}
                      name={`signers[${i}].idCardFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('DEATHS_FORM:ID_CARD_FILE'),
                          value: files?.signersIdCardFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={
                        isEditing || !isMandatory
                          ? fileValidation
                          : composeValidators(requiredValidation, fileValidation)
                      }
                    />
                    <FieldType
                      label={i18next.t<string>('DEATHS_FORM:ID_CARD_BACK_FILE')}
                      name={`signers[${i}].idCardBackFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('DEATHS_FORM:ID_CARD_BACK_FILE'),
                          value: files?.signersIdCardBackFiles?.[i],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={
                        isEditing || !isMandatory
                          ? fileValidation
                          : composeValidators(requiredValidation, fileValidation)
                      }
                    />
                  </FormRowContainer>

                  <FormRowContainer>
                    <FormRowContainer>
                      <FieldType
                        label={i18next.t<string>('DEATHS_FORM:AREA_CODE')}
                        maxLength={4}
                        name={`signers[${i}].areaCode`}
                        parse={(v: string) => digitsOnly(v)}
                        render={Input}
                        validate={isMandatory ? requiredValidation : undefined}
                      />
                      <FieldType
                        label={i18next.t<string>('DEATHS_FORM:PHONE')}
                        maxLength={8}
                        name={`signers[${i}].phone`}
                        parse={(v: string) => digitsOnly(v)}
                        render={Input}
                        validate={isMandatory ? requiredValidation : undefined}
                        helperText="Incluir código de área, sin el prefijo '15' para celulares"
                      />
                    </FormRowContainer>
                    <Field
                      label={i18next.t<string>('DEATHS_FORM:TELEPHONE')}
                      name={`signers[${i}].telephone`}
                      parse={(value: string) => value}
                      render={Input}
                      validate={phoneNumberValidation}
                    />
                  </FormRowContainer>

                  <FormRowContainer>
                    <FieldType
                      label={i18next.t<string>('DEATHS_FORM:ADDRESS')}
                      name={`signers[${i}].address`}
                      parse={(value: string) => value}
                      render={Input}
                      validate={isMandatory ? requiredValidation : undefined}
                    />
                    <FieldType
                      label={i18next.t<string>('DEATHS_FORM:KINSHIP')}
                      name={`signers[${i}].kinship`}
                      parse={(value: string) => value}
                      render={Input}
                      validate={
                        isMandatory
                          ? composeValidators(requiredValidation, onlyLetters)
                          : onlyLetters
                      }
                    />
                  </FormRowContainer>
                  <br />
                </Fragment>
              );
            })}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="payment-page">
            <Typography variant="h6">
              {i18next.t<string>('DEATHS_FORM:PAYMENT_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <Field
                label={i18next.t<string>('DEATHS_FORM:CURRENCY')}
                name="payment.currency"
                options={CURRENCY_OPTIONS}
                render={SelectInput}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:AMOUNT')}
                name="payment.amount"
                render={Input}
                validate={requiredValidation}
                type="number"
              />
            </FormRowContainer>
            <FieldRequired
              label={i18next.t<string>('DEATHS_FORM:PAYMENT_CONDITIONS')}
              name="payment.paymentConditions"
              render={Input}
              validate={composeValidators(requiredValidation, onlyLetters)}
            />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:PAYMENTS_DONE')}
                name="payment.paymentsDone"
                render={Input}
                validate={requiredValidation}
                type="number"
              />
              <FieldRequired
                label={i18next.t<string>('DEATHS_FORM:GARAGE')}
                name="payment.garage"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('DEATHS_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
          {!isEditing && (
            <MultiStepForm.Page key="review-page">
              <ProcedureReviewStep sections={reviewSections} />
            </MultiStepForm.Page>
          )}
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default DeathsForm;
